import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Search } from 'lucide-react';
import { cn } from '@mlm/ui';
import type { PosPriceMap, PosProduct, PosVariant } from '../../lib/api';
import { CHIP_ALL, activeChip, categoryChips, filterByChip } from '../../lib/category-chips';
import { exactSkuMatch, filterPosProducts } from '../../lib/pos-search';
import { CategoryChips } from './category-chips';
import { ProductTile } from './product-tile';

/**
 * The left pane of the till: title, search, category chips and the tile grid.
 *
 * Search is client-side: `GET /pos/products` returns the whole branch catalog
 * in one unpaginated array, so there is nothing to fetch. The box doubles as
 * the barcode input — a scanner types the SKU and sends Enter, which adds the
 * item and clears the field so the next scan lands clean.
 */
export function Catalog({
  title,
  actions,
  products,
  loading,
  priceMap,
  onTap,
  onScan,
}: {
  title: string;
  /** Right side of the header (branch picker, free-slot activation). */
  actions?: ReactNode;
  products: PosProduct[] | undefined;
  loading: boolean;
  priceMap: PosPriceMap | null;
  /** Tile tap: opens the item sheet for a variation product. */
  onTap: (p: PosProduct) => void;
  /** Barcode/SKU hit. `variant` is non-null when a variation's own SKU matched. */
  onScan: (p: PosProduct, variant: PosVariant | null) => void;
}) {
  const [q, setQ] = useState('');
  const [chip, setChip] = useState(CHIP_ALL);
  const inputRef = useRef<HTMLInputElement>(null);
  const all = useMemo(() => products ?? [], [products]);
  const chips = useMemo(() => categoryChips(all), [all]);
  const selectedChip = activeChip(chips, chip);
  // Photo tiles only when the catalog has photos. Most catalogs here were
  // entered without any, and for those the dense tiles show four times as many
  // products per screen.
  const compact = useMemo(() => !all.some((p) => p.images.length > 0), [all]);
  // A typed search looks across the WHOLE catalog, whatever chip is lit: a
  // cashier on "Pastries" who types "latte" means the latte, not "no results".
  const searching = q.trim() !== '';
  const rows = useMemo(
    () => (searching ? filterPosProducts(all, q) : filterByChip(all, selectedChip)),
    [all, q, searching, selectedChip],
  );

  // Keys typed while nothing is focused go to the search box. That is what
  // keeps a keyboard-wedge barcode scanner working: it types the SKU and sends
  // Enter into whatever has focus, and on a touch till nothing does (the box
  // is deliberately NOT auto-focused, which would pop the on-screen keyboard
  // on every visit). Focusing during keydown lands that very character in the
  // box. F2 focuses it explicitly. Neither fires while the cashier is typing
  // in another field or a dialog is open.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = document.activeElement as HTMLElement | null;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) {
        return;
      }
      if (document.querySelector('[role="dialog"]')) return;
      if (e.key === 'F2') {
        e.preventDefault();
        inputRef.current?.focus();
        return;
      }
      const printable = e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;
      if (printable && e.key !== ' ') inputRef.current?.focus();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setQ('');
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();

    // A scanned variation SKU has already chosen the variation, so add it
    // directly; a scanned parent SKU still goes through the item sheet.
    const hit = exactSkuMatch(all, q);
    if (hit) {
      if (hit.variant) onScan(hit.product, hit.variant);
      else onTap(hit.product);
      setQ('');
      return;
    }
    // Not a scan — but if the filter narrowed to exactly one single-SKU
    // product, Enter is unambiguous, so take it.
    if (rows.length === 1 && rows[0]!.variants.length === 0) {
      onTap(rows[0]!);
      setQ('');
    }
  }

  return (
    <section className="flex min-w-0 flex-1 flex-col gap-4">
      <header className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <label className="flex h-12 min-w-[220px] flex-1 items-center gap-3 rounded-[15px] bg-muted px-[19px]">
          <Search className="h-5 w-5 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Scan barcode or search name / SKU…  (F2)"
            aria-label="Search products"
            className="h-full w-full min-w-0 bg-transparent text-base outline-none placeholder:text-muted-foreground"
          />
        </label>
        {actions}
      </header>

      <CategoryChips
        chips={chips}
        selected={searching ? '' : selectedChip}
        onSelect={(id) => {
          // Picking a category is leaving the search, not narrowing it.
          setChip(id);
          setQ('');
        }}
      />

      {loading ? (
        <p className="text-base text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-base text-muted-foreground">
          {searching
            ? `No products match “${q.trim()}”.`
            : all.length === 0
              ? 'No products in this branch.'
              : 'No products in this category.'}
        </p>
      ) : (
        <div
          className={cn(
            'grid gap-[5px] rounded-[10px] bg-muted p-[5px]',
            compact
              ? 'grid-cols-[repeat(auto-fill,minmax(180px,1fr))]'
              : 'grid-cols-[repeat(auto-fill,minmax(140px,1fr))]',
          )}
        >
          {rows.map((p) => (
            <ProductTile
              key={p.id}
              product={p}
              priceMap={priceMap}
              onTap={onTap}
              compact={compact}
            />
          ))}
        </div>
      )}
    </section>
  );
}
